import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import AppApi from '@/apis/app-api';
import {
  BlindReviewApi,
  CreateModifyReasonLogDto,
  GetBlindReviewLogsQuery,
  GetModifyReasonLogsQuery,
} from '@/apis/blind-review.api';
import { baseUrl } from '@/consts/route';
import { cleanArray } from '@/utils/cleanArray';

import { useAuth } from './useAuth';

type UseBlindReviewLogsOptions = {
  enabled?: boolean;
  /** polling interval (ms) - false면 polling 안함 */
  refetchInterval?: number | false;
};

/**
 * 블라인드 신청 내역 조회
 */
export const useBlindReviewLogs = (
  query?: GetBlindReviewLogsQuery,
  options?: UseBlindReviewLogsOptions,
) => {
  const { accessToken } = useAuth();
  const api = new AppApi(baseUrl);
  const blindReviewApi = new BlindReviewApi(api);

  const hasQuery = !!(query?.user_id || query?.review_id || query?.shop_id || query?.log_id);

  return useQuery({
    queryKey: cleanArray(['blind_review_logs', JSON.stringify(query)]),
    queryFn: async () => {
      if (!accessToken) return null;
      api.setup(accessToken);
      return await blindReviewApi.getBlindReviewLogs(query);
    },
    enabled: !!accessToken && hasQuery && (options?.enabled ?? true),
    refetchInterval: options?.refetchInterval,
    staleTime: 0,
  });
};

/**
 * 블라인드 신청
 */
export const useBlindReviewRequest = () => {
  const { accessToken, userId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['blind_review_request'],
    mutationFn: async ({ reviewId }: { reviewId: string }) => {
      if (!accessToken || !userId) {
        throw new Error('인증이 필요합니다.');
      }

      const api = new AppApi(baseUrl);
      api.setup(accessToken);
      const blindReviewApi = new BlindReviewApi(api);

      return await blindReviewApi.requestBlindReview(reviewId);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['blind_review_logs'],
      });
      await queryClient.refetchQueries({
        queryKey: ['reviews'],
      });
      await queryClient.refetchQueries({
        queryKey: ['reviews_infinity'],
      });
      await queryClient.refetchQueries({
        queryKey: ['review'],
      });
    },
  });
};

/**
 * AI 사유 갱신 (비동기)
 */
export const useBlindReviewRefreshReason = () => {
  const { accessToken, userId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['blind_review_refresh_reason'],
    mutationFn: async ({ reviewId }: { reviewId: string }) => {
      if (!accessToken || !userId) {
        throw new Error('인증이 필요합니다.');
      }

      const api = new AppApi(baseUrl);
      api.setup(accessToken);
      const blindReviewApi = new BlindReviewApi(api);

      return await blindReviewApi.refreshReason(reviewId);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['blind_review_logs'],
      });
    },
  });
};

/**
 * 블라인드 신청 내역 삭제 (개발용)
 */
export const useBlindReviewDelete = () => {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['blind_review_delete'],
    mutationFn: async ({ reviewId }: { reviewId: string }) => {
      if (!accessToken) {
        throw new Error('인증이 필요합니다.');
      }

      const api = new AppApi(baseUrl);
      api.setup(accessToken);
      const blindReviewApi = new BlindReviewApi(api);

      return await blindReviewApi.deleteBlindReviewLog(reviewId);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['blind_review_logs'],
      });
      await queryClient.refetchQueries({
        queryKey: ['reviews'],
      });
      await queryClient.refetchQueries({
        queryKey: ['reviews_infinity'],
      });
      await queryClient.refetchQueries({
        queryKey: ['review'],
      });
    },
  });
};

/**
 * 블라인드 신청 내역 수정 로그 생성
 */
export const useBlindReviewCreateModifyLog = () => {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['blind_review_create_modify_log'],
    mutationFn: async (body: CreateModifyReasonLogDto) => {
      if (!accessToken) {
        throw new Error('인증이 필요합니다.');
      }

      const api = new AppApi(baseUrl);
      api.setup(accessToken);
      const blindReviewApi = new BlindReviewApi(api);

      return await blindReviewApi.createModifyLog(body);
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: ['blind_review_logs'],
      });
      await queryClient.refetchQueries({
        queryKey: ['blind_review_modify_logs'],
      });
    },
  });
};

type UseBlindReviewModifyLogsOptions = {
  enabled?: boolean;
};

/**
 * 블라인드 신청 내역 수정 로그 조회
 */
export const useBlindReviewModifyLogs = (
  query?: GetModifyReasonLogsQuery,
  options?: UseBlindReviewModifyLogsOptions,
) => {
  const { accessToken } = useAuth();
  const api = new AppApi(baseUrl);
  const blindReviewApi = new BlindReviewApi(api);

  const hasQuery = !!(query?.user_id || query?.log_id);

  return useQuery({
    queryKey: cleanArray(['blind_review_modify_logs', JSON.stringify(query)]),
    queryFn: async () => {
      if (!accessToken) return null;
      api.setup(accessToken);
      return await blindReviewApi.getModifyLogs(query);
    },
    enabled: !!accessToken && hasQuery && (options?.enabled ?? true),
    staleTime: 0,
  });
};
